/**
 * Formata um valor numérico para exibição em gráficos
 * @param value O valor a ser formatado
 * @returns String formatada para exibição 
 */
export const formatChartValue = (value: number): string => {
  // Se o valor for nulo, indefinido ou NaN, retornar '0'
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  
  // Para valores zero, retornar '0' diretamente
  if (value === 0) {
    return '0';
  }
  
  // Arredondar para evitar problemas de precisão
  const roundedValue = Math.round(value * 100) / 100;
  
  // Para valores menores que 1000, mostrar o valor com até 2 casas decimais
  if (roundedValue < 1000) {
    // Usar vírgula como separador decimal, sem separador de milhar
    return roundedValue.toLocaleString('pt-BR', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 2,
      useGrouping: false // Evita usar separador de milhar
    });
  }
  
  // Para valores maiores que 1000 e menores que 1000000, formatação mais compacta
  if (roundedValue < 1000000) {
    const thousands = roundedValue / 1000;
    // Formatação com K para milhares, sem separador de milhar no número
    return thousands.toLocaleString('pt-BR', { 
      minimumFractionDigits: 0, 
      maximumFractionDigits: 1,
      useGrouping: false // Evita usar separador de milhar
    }) + 'K';
  }
  
  // Para valores maiores que 1000000, formatação com M
  const millions = roundedValue / 1000000;
  return millions.toLocaleString('pt-BR', { 
    minimumFractionDigits: 0, 
    maximumFractionDigits: 1,
    useGrouping: false // Evita usar separador de milhar
  }) + 'M';
};

/**
 * Formata valores para gráficos de pizza
 * @param value O valor a ser formatado
 * @returns String formatada para exibição
 */
export const formatPieChartValue = (value: number): string => {
  // Se o valor for nulo, indefinido ou NaN, retornar '0'
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  
  // Para valores zero, retornar '0' diretamente
  if (value === 0) {
    return '0';
  }
  
  // Para valores muito pequenos, mostrar pelo menos '1'
  if (value < 1) {
    return '1';
  }
  
  // Arredondar para o inteiro mais próximo
  const roundedValue = Math.round(value);
  
  // Para valores menores que 1000, mostrar o valor inteiro
  if (roundedValue < 1000) {
    return roundedValue.toString();
  }
  
  // Para valores maiores que 1000, usar formato compacto
  if (roundedValue < 1000000) {
    const thousands = Math.round(roundedValue / 100) / 10;
    return thousands.toString() + 'K';
  }
  
  // Para valores maiores que 1000000, usar formato com M
  const millions = Math.round(roundedValue / 100000) / 10;
  return millions.toString() + 'M';
};

/**
 * Calcula a altura percentual para barras de gráficos
 * @param value Valor a ser convertido em altura percentual
 * @param maxValue Valor máximo de referência
 * @returns String representando a altura percentual (ex: '45%')
 */
export const calculateBarHeight = (value: number, maxValue: number): string => {
  // Se o valor for zero, nulo ou não definido, retorna uma altura mínima para visualização
  if (value === null || value === undefined || isNaN(value) || value <= 0) {
    return '2%'; // Altura mínima reduzida em porcentagem
  }
  
  // Se o maxValue for inválido, define um valor padrão
  if (!maxValue || maxValue <= 0 || isNaN(maxValue)) {
    maxValue = 100;
  }
  
  // Usando escala linear para representar corretamente as proporções
  // Com uma altura mínima garantida para valores muito pequenos
  const minHeightPercent = 5; // Altura mínima para qualquer valor > 0
  
  // Calcula a proporção direta
  const normalizedValue = value / maxValue;
  
  // Aplica a proporção direta ao intervalo disponível (5% a 95%)
  const percentage = minHeightPercent + (95 - minHeightPercent) * normalizedValue;
  
  // Retorna a porcentagem para ocupar o espaço proporcional ao valor
  return `${percentage}%`;
};

/**
 * Gera uma cor baseada no índice e nas cores da marca Westetics
 * @param index Índice do item
 * @returns Classe CSS Tailwind para cor
 */
export const getColorClass = (index: number): string => {
  const colors = [
    'bg-indigo-600',    // Azul escuro
    'bg-red-500',       // Vermelho
    'bg-emerald-500',   // Verde esmeralda
    'bg-amber-500',     // Âmbar/Amarelo
    'bg-violet-600',    // Violeta
    'bg-rose-500',      // Rosa
    'bg-cyan-500',      // Ciano
    'bg-orange-500'     // Laranja
  ];
  
  return colors[index % colors.length];
};

/**
 * Gera uma cor para texto baseada no índice e nas cores da marca Westetics
 * @param index Índice do item
 * @returns Classe CSS Tailwind para cor de texto
 */
export const getTextColorClass = (index: number): string => {
  const colors = [
    'text-indigo-600',  // Azul escuro
    'text-red-500',     // Vermelho
    'text-emerald-500', // Verde esmeralda
    'text-amber-500',   // Âmbar/Amarelo
    'text-violet-600',  // Violeta
    'text-rose-500',    // Rosa
    'text-cyan-500',    // Ciano
    'text-orange-500'   // Laranja
  ];
  
  return colors[index % colors.length];
};

/**
 * Retorna uma cor hexadecimal baseada no índice
 * Utilizado para elementos SVG onde classes CSS não funcionam
 * @param index Índice do item
 * @returns String em formato de cor hexadecimal
 */
export const getHexColor = (index: number): string => {
  const colors = [
    '#4F46E5',  // indigo-600 (Azul escuro)
    '#EF4444',  // red-500 (Vermelho)
    '#10B981',  // emerald-500 (Verde esmeralda)
    '#F59E0B',  // amber-500 (Âmbar/Amarelo)
    '#8B5CF6',  // violet-600 (Violeta)
    '#F43F5E',  // rose-500 (Rosa)
    '#06B6D4',  // cyan-500 (Ciano)
    '#F97316'   // orange-500 (Laranja)
  ];
  
  return colors[index % colors.length];
};

/**
 * Calcula o percentual de um valor sobre o total
 * @param value Valor
 * @param total Total
 * @returns String formatada como percentual (ex: '45%')
 */
export const calculatePercentage = (value: number, total: number): string => {
  if (!value || value <= 0 || !total || total <= 0) {
    return '0%';
  }
  
  const percentage = (value / total) * 100;
  return `${Math.round(percentage)}%`;
};

/**
 * Formata um valor numérico para exibição em formato monetário (R$)
 * @param value O valor a ser formatado
 * @returns String formatada como moeda (ex: 'R$ 1.234,56')
 */
export const formatCurrencyValue = (value: number): string => {
  // Se o valor for nulo, indefinido ou NaN, retornar 'R$ 0'
  if (value === null || value === undefined || isNaN(value)) {
    return 'R$ 0';
  }
  
  // Para valores zero, retornar 'R$ 0' diretamente
  if (value === 0) {
    return 'R$ 0';
  }
  
  // Para valores menores que 1000, mostrar o valor com 2 casas decimais
  if (Math.abs(value) < 1000) {
    return 'R$ ' + value.toLocaleString('pt-BR', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2
    });
  }
  
  // Para valores maiores que 1000 e menores que 1000000, formatação mais compacta com K
  if (Math.abs(value) < 1000000) {
    const thousands = value / 1000;
    return 'R$ ' + thousands.toLocaleString('pt-BR', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2
    }) + 'K';
  }
  
  // Para valores maiores que 1000000, formatação com M
  const millions = value / 1000000;
  return 'R$ ' + millions.toLocaleString('pt-BR', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2
  }) + 'M';
};

/**
 * Gera uma cor baseada no índice usando as cores personalizadas para gráficos
 * @param index Índice do item
 * @returns Classe CSS Tailwind para cor
 */
export const getChartColorClass = (index: number): string => {
  // Cores roxas e laranjas conforme especificado
  const colors = [
    'bg-[#5a3c60]',  // Westetics Purple 400
    'bg-[#FFA06B]',  // Orange 300
    'bg-[#68205D]',  // Purple 700
    'bg-[#FB8948]',  // Orange 400
    'bg-[#441745]',  // Purple 800
    'bg-[#D36F42]',  // Orange 500
    'bg-[#200F2C]',  // Purple 900
    'bg-[#AB5E3C]'   // Orange 600
  ];
  
  return colors[index % colors.length];
};

/**
 * Gera uma cor para texto baseada no índice usando as cores personalizadas para gráficos
 * @param index Índice do item
 * @returns Classe CSS Tailwind para cor de texto
 */
export const getChartTextColorClass = (index: number): string => {
  // Cores roxas e laranjas conforme especificado
  const colors = [
    'text-[#8C2876]',  // Purple 600
    'text-[#FFA06B]',  // Orange 300
    'text-[#68205D]',  // Purple 700
    'text-[#FB8948]',  // Orange 400
    'text-[#441745]',  // Purple 800
    'text-[#D36F42]',  // Orange 500
    'text-[#200F2C]',  // Purple 900
    'text-[#AB5E3C]'   // Orange 600
  ];
  
  return colors[index % colors.length];
};

/**
 * Retorna uma cor hexadecimal baseada no índice usando as cores personalizadas para gráficos
 * Utilizado para elementos SVG onde classes CSS não funcionam
 * @param index Índice do item
 * @returns String em formato de cor hexadecimal
 */
export const getChartHexColor = (index: number): string => {
  // Cores roxas conforme paleta westetics-purple
  const colors = [
    "#5a3c60", // Westetics Purple 400 (base)
    "#6b4d71",
    "#7c5e82",
    "#8d6f93",
    "#9e80a4",
    "#a692ba",
    "#b7aeba",
    "#c8c0cb",
    "#d9d2dc",
    "#e8e5e9", // Lilás mais claro
  ];
  
  return colors[index % colors.length];
};